"use client"

import React, {useEffect, useLayoutEffect, useRef} from "react";
import {motion, useAnimationControls, useReducedMotion} from "framer-motion";
import {cn} from "@/lib/utils";
import Image from "next/image";
import gsap from "gsap";
import {ScrollTrigger} from "gsap/ScrollTrigger";
gsap.registerPlugin(ScrollTrigger);

import people from "@/public/animatedAssets/people.png";
import basti from "@/public/animatedAssets/basti.png";
import rocket from "@/public/animatedAssets/rocket.png";
import usdt from "@/public/animatedAssets/usdt.svg";
import xrp from "@/public/animatedAssets/xrp.svg";
import trx from "@/public/animatedAssets/trx.svg";
import bnb from "@/public/animatedAssets/bnb.svg";

type State = "collapsed" | "expanded";

export const AnimatedAsset1 = ({
  className = "",
  language,
}: {
  className?: string;
  language: "de" | "en";
}) => {
  const controls = useAnimationControls();
  const prefersReduced = useReducedMotion();
  const stateRef = useRef<State>("collapsed");
  const rootRef = useRef<HTMLDivElement | null>(null);

  const setState = (next: State) => {
    if (stateRef.current === next) return Promise.resolve();
    stateRef.current = next;
    return controls.start(next);
  };

  useEffect(() => {
    controls.set("collapsed");
    stateRef.current = "collapsed";
  }, [controls]);

  useLayoutEffect(() => {
    if (prefersReduced) return;
    const el = rootRef.current;
    if (!el) return;

    const isTouchDevice = window.matchMedia("(pointer: coarse)").matches;

    if (isTouchDevice) {
      const st = ScrollTrigger.create({
        trigger: el,
        start: "top 50%",
        end: "bottom 50%",
        onEnter: () => setState("expanded"),
        onLeave: () => {},
        onEnterBack: () => setState("expanded"),
        onLeaveBack: () => setState("collapsed"),
        invalidateOnRefresh: true,
      });

      return () => st.kill();
    }

    return;
  }, [prefersReduced, controls]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMouseEnter = async () => {
    if (prefersReduced) return;
    await setState("expanded");
  };

  const handleMouseLeave = () => {
    if (prefersReduced) return;
    setState("collapsed");
  };

  const ease = {ease: "circOut", duration: .75};

  const chatBubble1Variants = {
    collapsed: {y: "5rem", transition: ease},
    expanded: {y: 0, transition: ease},
  }

  const chatBubble2Variants = {
    collapsed: {y: "8rem", opacity: 0, transition: ease},
    expanded: {y: 0, opacity: 1, transition: ease},
  }

  const rotateVariants = {
    collapsed: {rotate: -2, x: "-.5rem", transition: ease},
    expanded: {rotate: 0, x: 0, transition: ease},
  }

  const chipVariants1 = {
    collapsed: {rotate: -2, y: "1.5rem", x: "-1rem", transition: ease},
    expanded: {rotate: 0, y: 0, x: 0, transition: ease},
  }

  const chipVariants2 = {
    collapsed: {rotate: -2, y: "1.5rem", x: "1rem", transition: ease},
    expanded: {rotate: 0, y: 0, x: 0, transition: ease},
  }

  const chipVariants3 = {
    collapsed: {rotate: -2, y: "2rem", x: "-1rem", transition: ease},
    expanded: {rotate: 0, y: 0, x: 0, transition: ease},
  }

  const chipVariants4 = {
    collapsed: {rotate: -2, y: "2rem", x: "1rem", transition: ease},
    expanded: {rotate: 0, y: 0, x: 0, transition: ease},
  }

  return (
    <div
      ref={rootRef}
      className={cn(
        "w-full h-[50rem] lg:h-full bg-[#0A1A1D] flex flex-col items-center justify-center px-[4.5rem] sm:px-[4rem] relative",
        className
      )}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="flex flex-col gap-[2rem] w-[min(41.5rem,100%)] mx-auto">
        <motion.div
          className="flex items-start gap-[1rem]"
          variants={chatBubble1Variants}
          animate={controls}
          initial="collapsed"
        >
          <Image
            className="!w-[13rem] flex-shrink-0"
            src={people}
            alt=""
          />

          <div className="bg-white relative rounded-tr-[12px] rounded-br-[12px] rounded-bl-[12px] rounded-tl-[3px] px-[2rem] py-[1.35rem] border border-white/20">
            <p className="mb-0 text-[2.25rem] leading-[1.3] text-black">
              {language === "en" ? "Which coin should be traded currently?" : "Welchen Coin sollten man aktuell traden?"}
            </p>
          </div>
        </motion.div>

        <motion.div
          className="flex items-end gap-[1rem]"
          variants={chatBubble2Variants}
          animate={controls}
          initial="collapsed"
        >
          <div className="flex flex-col items-start grow gap-[1rem]">
            <motion.div
              className="bg-[#D9F9B9] relative rounded-tr-[12px] rounded-br-[3px] rounded-bl-[12px] rounded-tl-[12px] px-[2rem] py-[1.35rem] border border-white/20"
              variants={rotateVariants}
              animate={controls}
              initial="collapsed"
            >
              <p className="mb-0 text-[2.25rem] leading-[1.3] text-black w-[92%]">
                {language === "en" ? "Bitcoin is currently on the verge of a breakout. I am extremely bullish." : "Bitcoin steht aktuell kurz vor dem Ausbruch. Ich bin extrem bullisch"}
                <Image
                  className="inline-block !w-[2.5rem] flex-shrink-0 mx-[.5rem]"
                  src={rocket}
                  alt=""
                />
              </p>
            </motion.div>

            <div className="flex gap-[1rem] flex-wrap">
              <motion.div
                className="flex gap-[1rem]"
                variants={chipVariants1}
                animate={controls}
                initial="collapsed"
              >
                <div className="border border-[#26A17B]/30 rounded-full bg-[#26A17B]/10 p-[.75rem] flex items-center gap-[1rem]">
                  <p className="mb-0 leading-none text-white text-[1.75rem] pl-[1rem]">USDT</p>

                  <Image
                    className="!w-[4rem] flex-shrink-0"
                    src={usdt}
                    alt=""
                  />
                </div>
              </motion.div>

              <motion.div
                className="flex gap-[1rem]"
                variants={chipVariants2}
                animate={controls}
                initial="collapsed"
              >
                <div className="border border-white/30 rounded-full bg-white/10 p-[.75rem] flex items-center gap-[1rem]">
                  <p className="mb-0 leading-none text-white text-[1.75rem] pl-[1rem]">XRP</p>

                  <Image
                    className="!w-[4rem] flex-shrink-0"
                    src={xrp}
                    alt=""
                  />
                </div>
              </motion.div>

              <motion.div
                className="flex gap-[1rem]"
                variants={chipVariants3}
                animate={controls}
                initial="collapsed"
              >
                <div className="border border-[#FF3E5D]/30 rounded-full bg-[#FF3E5D]/10 p-[.75rem] flex items-center gap-[1rem]">
                  <p className="mb-0 leading-none text-white text-[1.75rem] pl-[1rem]">XRP</p>

                  <Image
                    className="!w-[4rem] flex-shrink-0"
                    src={trx}
                    alt=""
                  />
                </div>
              </motion.div>

              <motion.div
                className="flex gap-[1rem]"
                variants={chipVariants4}
                animate={controls}
                initial="collapsed"
              >
                <div className="border border-[#F3BA2F]/30 rounded-full bg-[#F3BA2F]/10 p-[.75rem] flex items-center gap-[1rem]">
                  <p className="mb-0 leading-none text-white text-[1.75rem] pl-[1rem]">XRP</p>

                  <Image
                    className="!w-[4rem] flex-shrink-0"
                    src={bnb}
                    alt=""
                  />
                </div>
              </motion.div>
            </div>
          </div>

          <motion.div
            className="!w-[5rem] flex-shrink-0"
            variants={rotateVariants}
            animate={controls}
            initial="collapsed"
          >
            <Image
              className="!w-[5rem] rounded-full border border-[#0A1A1D]"
              src={basti}
              alt=""
            />
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};